import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { Alert, Button, Input, Modal } from '@/components/ui'
import { useCreateBankOpeningBalance } from '@/hooks/useAccounting'
import { translateApiError } from '@/lib/apiError'

export function OpeningBalanceModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')
  const createBalance = useCreateBankOpeningBalance()
  const [balance, setBalance] = useState('')
  const [asOfDate, setAsOfDate] = useState('')
  const [error, setError] = useState('')
  const [alreadyPosted, setAlreadyPosted] = useState(false)
  const close = () => { if (!createBalance.isPending) onClose() }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (createBalance.isPending || alreadyPosted) return
    if (!balance.trim() || !Number.isFinite(Number(balance))) {
      setError(t('opening_balance.invalid_balance'))
      return
    }
    if (!asOfDate) {
      setError(t('opening_balance.date_required'))
      return
    }
    setError('')
    try {
      await createBalance.mutateAsync({ balance: Number(balance), as_of_date: asOfDate })
      onClose()
    } catch (cause) {
      setAlreadyPosted(isAxiosError(cause) && cause.response?.status === 409)
      setError(translateApiError(cause, 'accounting', { t, tc }))
    }
  }

  return (
    <Modal isOpen onClose={close} title={t('opening_balance.title')} size="sm" closeOnOverlay={!createBalance.isPending}
      footer={<>
        <Button variant="ghost" disabled={createBalance.isPending} onClick={close}>{tc('actions.cancel')}</Button>
        <Button type="submit" form="bank-opening-balance-form" loading={createBalance.isPending} disabled={alreadyPosted}>{tc('actions.save')}</Button>
      </>}
    >
      <form id="bank-opening-balance-form" aria-label={t('opening_balance.title')} onSubmit={submit} className="space-y-4">
        <p className="text-sm text-base-content/60">{t('opening_balance.description')}</p>
        {error && <Alert variant="error" role="alert">{error}</Alert>}
        <Input id="opening-balance-amount" label={t('opening_balance.balance')} type="number" inputMode="decimal" step="0.01" required
          leftIcon={<span aria-hidden="true">฿</span>} value={balance} disabled={createBalance.isPending || alreadyPosted} onChange={(event) => setBalance(event.target.value)} />
        <Input id="opening-balance-date" label={t('opening_balance.as_of_date')} type="date" required
          value={asOfDate} disabled={createBalance.isPending || alreadyPosted} onChange={(event) => setAsOfDate(event.target.value)} />
      </form>
    </Modal>
  )
}
